import { ImageHit } from '../models/image-result.model';

/** Labels for metadata.detected, singular / plural. */
const DETECTED: { key: 'persons' | 'faces' | 'text' | 'animals' | 'sports' | 'vehicles'; one: string; many: string }[] = [
  { key: 'persons', one: 'person', many: 'persons' },
  { key: 'faces', one: 'face', many: 'faces' },
  { key: 'text', one: 'text area', many: 'text areas' },
  { key: 'animals', one: 'animal', many: 'animals' },
  { key: 'sports', one: 'sport', many: 'sports' },
  { key: 'vehicles', one: 'vehicle', many: 'vehicles' },
];

/** The colour names the image analysis uses, as swatches. */
const SWATCH: Record<string, string> = {
  black: '#1F2430', white: '#F4F5F7', grey: '#9AA1AE', gray: '#9AA1AE', red: '#E0483E',
  orange: '#F08A33', yellow: '#F2C94C', green: '#4CAF6A', turquoise: '#3CC3C0', blue: '#3B73E8',
  purple: '#8A5CD6', pink: '#EC7FB0', brown: '#8C5A3C',
};

/** What each /image response key means, for the hover tips (from real
 *  responses; /image's metadata isn't in the API docs yet). */
export const IMAGE_TIPS = {
  url: 'The image, as Cikisi stores it.',
  originalUrl: 'Where the image was originally published (metadata.original_url).',
  indexationDate: 'When Cikisi indexed the image.',
  articleTitle: 'The article the image appeared in (metadata.article.title).',
  domainName: 'The site the article was published on.',
  publicationDate: 'When the article was published.',
  dimensions: 'The stored image size in pixels (metadata.dimensions).',
  detected: 'What the image analysis found in it (metadata.detected).',
  colors: 'Dominant colours and their share of the image (metadata.colors).',
  ocr: 'Text read off the image (metadata.ocr).',
  caption: "The photo's own caption and credit, when the agency supplied one.",
  count: 'Images returned, after Max results was applied.',
  totalMatches: 'How many images matched before the Max results cut.',
} as const;

/** One image, ready for the gallery and the lightbox. */
export interface ImageView {
  index: number;
  hit: ImageHit;
  /** width / height from metadata.dimensions, else a common 3 / 2. */
  aspect: number;
  /** Non-zero detections, e.g. "10 persons". */
  detected: string[];
  /** "AP · Evan Vucci · Washington, DC, USA", from whichever parts came back. */
  credit: string | null;
  colors: { name: string; ratio: number; swatch: string }[];
  /** The colour bar, read out: "black 64%, red 11%". */
  colorLabel: string;
  /** A short accessible name: the article title, else the caption, else the site. */
  label: string;
}

export function toImageView(hit: ImageHit, index: number): ImageView {
  const m = hit.metadata;
  const d = m?.detected;
  const colors = (m?.colors ?? []).map(c => ({ ...c, swatch: SWATCH[c.name.toLowerCase()] ?? '#C5CAD4' }));
  return {
    index,
    hit,
    aspect: m?.width && m?.height ? m.width / m.height : 3 / 2,
    detected: d ? DETECTED.filter(x => d[x.key] > 0).map(x => `${d[x.key]} ${d[x.key] === 1 ? x.one : x.many}`) : [],
    credit: m ? [m.source, m.artist, m.place].filter(Boolean).join(' · ') || null : null,
    colors,
    colorLabel: colors.map(c => `${c.name} ${c.ratio}%`).join(', '),
    label: m?.article?.title || m?.description || m?.title || hit.host,
  };
}
